import { Link, Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/modules/auth/AuthContext';
import { useCanEnterAdmin } from '@/modules/admin/adminAccess';
import { resolveLanding } from './resolveLanding';
import { readLastVenueId, writeLastVenueId } from './lastVenue';

/**
 * The `/` route as a resolver, not a page (ADR 0015). It applies the single landing rule to
 * `me.venues` and the last-used Venue, redirecting straight into a workspace or, when
 * the choice is ambiguous, rendering the "choose venue" screen. No role special-casing —
 * a multi-Venue Admin follows the same rule as an Operator.
 *
 * The one exception is the empty set: a franchise admin with zero accessible Venues (the
 * fresh-bootstrap state, before any Venue exists) is sent to the Franchise admin area to
 * create the first Venue, rather than dead-ended on the "no access" chooser.
 */
export function LandingResolver() {
  const { venues } = useAuth();
  const canEnterAdmin = useCanEnterAdmin();
  const resolution = resolveLanding(venues, readLastVenueId());

  if (resolution.kind === 'enter') {
    return <Navigate to={`/loc/${resolution.venueId}`} replace />;
  }
  if (venues.length === 0 && canEnterAdmin) {
    return <Navigate to="/admin" replace />;
  }
  return <ChooseVenue />;
}

function ChooseVenue() {
  const { t } = useTranslation('common');
  const { venues } = useAuth();

  return (
    <section className="mx-auto grid max-w-md gap-6 p-6">
      <div className="grid gap-1">
        <h1 className="text-2xl font-semibold">{t('landing.title')}</h1>
        <p className="text-muted-foreground">{t('landing.subtitle')}</p>
      </div>
      {venues.length === 0 ? (
        <p className="text-muted-foreground">{t('landing.empty')}</p>
      ) : (
        <ul className="grid gap-2">
          {venues.map((venue) => (
            <li key={venue.id}>
              <Link
                to={`/loc/${venue.id}`}
                onClick={() => writeLastVenueId(venue.id)}
                className="flex items-center rounded-md border px-4 py-3 text-sm font-medium hover:bg-accent hover:text-foreground"
              >
                {venue.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
